import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { useAppSelector } from '../../../../../app/hooks';
import { useSaveTicket } from '../../../hooks/useSaveTicket';
import { ConflictResolutionDialog } from './ConflictResolutionDialog';

const SaveTicketButton: React.FC = () => {
  const [showValidationError, setShowValidationError] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const { currentTicket, isEdited } = useAppSelector(state => state.ticket);
  const {
    isSaving,
    saveStatus,
    setSaveStatus,
    conflict,
    save,
    setConflictChoice,
    cancelConflict,
    confirmConflict,
  } = useSaveTicket();

  const validateRequiredFields = (): boolean => {
    if (!currentTicket) return false;

    const { identification } = currentTicket;
    const missingFields: string[] = [];

    // Category is always required
    if (!identification.category?.trim()) {
      missingFields.push('Category');
    }

    // Title and Description are only required for General categories (when TicketInformationSection is shown)
    const requiresTicketInformation = identification.category === 'GEN' || !identification.category;

    if (requiresTicketInformation) {
      if (!identification.title?.trim()) {
        missingFields.push('Title');
      }
      
      if (!identification.description?.trim()) {
        missingFields.push('Description');
      }
    }

    // Validate Facility Maintenance specific fields
    if (identification.category === 'FAC') {
      const facilityRequest = currentTicket.facilityMaintenanceRequest;
      
      if (!facilityRequest?.center?.trim()) {
        missingFields.push('Center');
      }
      
      if (!facilityRequest?.title?.trim()) {
        missingFields.push('Maintenance Title');
      }
      
      if (!facilityRequest?.description?.trim()) {
        missingFields.push('Maintenance Description');
      }

      if (!facilityRequest?.issueType?.trim()) {
        missingFields.push('Issue Type');
      }
    }

    // TODO: Add validation for HST and CPSI access request fields when those categories are selected

    if (missingFields.length > 0) {
      const fieldList = missingFields.join(', ');
      const message = `Please complete the following required fields: ${fieldList}`;
      setValidationMessage(message);
      setShowValidationError(true);
      return false;
    }

    return true;
  };

  const handleCloseValidationError = () => {
    setShowValidationError(false);
  };

  const handleSave = async () => {
    if (!currentTicket || !isEdited) return;
    if (!validateRequiredFields()) return;
    await save();
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={handleSave}
        disabled={!isEdited || isSaving}
        startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save />}
        sx={{
          backgroundColor: '#162346',
          color: 'white',
          fontWeight: 600,
          '&:hover': {
            backgroundColor: '#1a2a52'
          },
          '&:disabled': {
            backgroundColor: '#64748b',
            color: '#ffffff'
          }
        }}
      >
        {isSaving ? 'Saving...' : 'Save Ticket'}
      </Button>

      {/* Validation Error Snackbar */}
      <Snackbar
        open={showValidationError}
        autoHideDuration={6000}
        onClose={handleCloseValidationError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseValidationError} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {validationMessage}
        </Alert>
      </Snackbar>

      {/* Save Status Snackbar */}
      <Snackbar
        open={!!saveStatus}
        autoHideDuration={4000}
        onClose={() => setSaveStatus(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSaveStatus(null)}
          severity={saveStatus?.type ?? 'info'}
          sx={{ width: '100%' }}
        >
          {saveStatus?.message}
        </Alert>
      </Snackbar>

      {/* Field-conflict resolution dialog (spec 010) */}
      {conflict && (
        <ConflictResolutionDialog
          conflict={conflict}
          onChoiceChange={setConflictChoice}
          onCancel={cancelConflict}
          onConfirm={confirmConflict}
          isResolving={isSaving}
        />
      )}
    </>
  );
};

export default SaveTicketButton;
